import { useCallback, useEffect } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { WorkspaceSelector, ChatArea } from '../../components/userDashboard/ui/index.js';
import { useDashboard, useVoiceRecording } from '../../components/userDashboard/hooks/index.js';
import { chatPageService } from '../../services/index.js';

export default function Chat() {
  const { activeUseCase, setActiveUseCase, fetchChatHistory, darkMode, chatHistory, isChatRenaming, onShareChat } = useOutletContext();
  const location = useLocation();
  const { conversationId, tokenConsumed, useCaseName, userUsecaseId, modelGroupId, modelGroupName } = location.state || {};

  const { 
    messages, 
    input, 
    setInput, 
    isTyping, 
    copiedId, 
    setCopiedId, 
    tokenUsage, 
    models,
    selectedModel, 
    setSelectedModel, 
    isModelDropdownOpen, 
    setIsModelDropdownOpen, 
    handleSendMessage, 
    handleTryAgain, 
    messagesEndRef,
    setMessages,
    setConversationId,
    parseHistoryMessages,
    conversationId: activeConversationId,
  } = useDashboard(activeUseCase, tokenConsumed ?? 0, conversationId ?? null, modelGroupId || sessionStorage.getItem('activeModelGroupId') || null);

  useEffect(() => {
    if (conversationId) setConversationId(conversationId);
  }, [conversationId]);

  const { isRecording, toggleRecording } = useVoiceRecording(setInput);

  const currentConversationId = conversationId;
  const isPinned = chatHistory?.find(c => (c.conversationId || c.ConversationId) === currentConversationId)?.isPinned ?? false;

  const handlePinChat = useCallback(async () => {
    if (!currentConversationId) return;
    try {
      await chatPageService.pinChat(currentConversationId, !isPinned);
      fetchChatHistory?.();
    } catch (error) { console.log('Pin error:', error); }
  }, [currentConversationId, isPinned, fetchChatHistory]);

  const handleShareChat = useCallback(async () => {
    if (!currentConversationId) return;
    try {
      await onShareChat?.(currentConversationId);
    } catch (error) { console.log('Share error:', error); }
  }, [currentConversationId, onShareChat]);

  const handleArchiveChat = useCallback(async () => {
    if (!currentConversationId) return;
    try {
      await chatPageService.archiveChat(currentConversationId, true);
      fetchChatHistory?.();
    } catch (error) { console.log('Archive error:', error); }
  }, [currentConversationId, fetchChatHistory]);

  const handleDeleteChat = useCallback(async () => {
    if (!currentConversationId) return;
    try {
      await chatPageService.deleteChat(currentConversationId);
      fetchChatHistory?.();
    } catch (error) { console.log('Delete error:', error); }
  }, [currentConversationId, fetchChatHistory]);

  useEffect(() => {
    if (conversationId) {
      const loadChat = async () => {
        try {
          const chatMessages = await chatPageService.getChatMessages(conversationId);
          const formattedMessages = parseHistoryMessages(chatMessages);
          console.log("formattedMessages after parsing:", formattedMessages);
          setActiveUseCase({ id: 'loaded', userUseCaseId: userUsecaseId, useCaseName: (useCaseName || 'General' )});
          setMessages(formattedMessages);
          if (modelGroupId) sessionStorage.setItem('activeModelGroupId', String(modelGroupId));
        } catch (error) {
          console.log('Error loading chat:', error);
        }
      };
      loadChat();
    }
  }, [conversationId, parseHistoryMessages, setActiveUseCase, setMessages, userUsecaseId, useCaseName]);

  const handleEdit = useCallback((msgId, newText) => {
    setMessages(prev => {
      const idx = prev.findIndex(m => m.messageId === msgId);
      if (idx === -1) return prev;
      return prev.slice(0, idx);
    });
    setInput(newText);
  }, [setMessages, setInput]);
  console.log('Chat component rendered with selectedModel:', selectedModel);
  return (
    <section className="flex-1 flex flex-col h-full overflow-hidden relative transition-all duration-300">
      {!activeUseCase ? (
        <WorkspaceSelector setActiveUseCase={setActiveUseCase} />
      ) : (
        <ChatArea 
          activeUseCase={activeUseCase} 
          selectedModel={selectedModel} 
          setSelectedModel={setSelectedModel} 
          isModelDropdownOpen={isModelDropdownOpen} 
          setIsModelDropdownOpen={setIsModelDropdownOpen} 
          messages={messages} 
          isTyping={isTyping} 
          copiedId={copiedId} 
          setCopiedId={setCopiedId} 
          handleTryAgain={handleTryAgain}
          handleEdit={handleEdit}
          messagesEndRef={messagesEndRef} 
          input={input} 
          setInput={setInput} 
          isRecording={isRecording} 
          toggleRecording={toggleRecording} 
          handleSendMessage={async (...args) => { await handleSendMessage(...args); fetchChatHistory?.(); }} 
          tokenUsage={tokenUsage} 
          darkMode={darkMode}
          onPinChat={handlePinChat}
          onArchiveChat={handleArchiveChat}
          onDeleteChat={handleDeleteChat}
          onShareChat={handleShareChat}
          isPinned={isPinned}
          hideHeaderAndInput={isChatRenaming}
          conversationId={activeConversationId}
          models={models}
        />
      )}
    </section>
  );
}
